import React, { useMemo } from 'react';
import { Alert, Button, Form } from 'antd';
import { useIntl } from 'umi';
import ContactFields, { ContactFieldValues, hasContactIdentity } from '@/components/Contacts/ContactFields';
import { ContactModel, ContactPayload } from '@/typings/models/Contact';
import { usePartnerDuplicateContact } from '@/components/Contacts/usePartnerDuplicateContact';
import DuplicateElsewhereNotice from '@/components/Contacts/DuplicateElsewhereNotice';
import { CONTACT_ENTITY, findDuplicateContact, getContactName } from '@/utils/contacts';
import { F, getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import styles from './index.module.less';
import { stopEscape } from './keyboard';

type NewContactStepProps = {
  initialValues: ContactFieldValues;
  /** The project's customer; its own contacts are checked through `knownContacts`. */
  customerId?: string;
  /** The customer's contacts (all on the project already), to check the email/phone against. */
  knownContacts: ContactModel[];
  roleSuggestions: string[];
  busy?: boolean;
  onRoleChange: (role: string) => void;
  onBack: () => void;
  /** Someone already there: the customer's own contact is shown, another one gets linked. */
  onLinkInstead: (contact: ContactModel) => void;
  onSubmit: (data: ContactPayload) => void;
};

const NewContactStep: React.FC<NewContactStepProps> = ({
  initialValues,
  customerId,
  knownContacts,
  roleSuggestions,
  busy,
  onRoleChange,
  onBack,
  onLinkInstead,
  onSubmit,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const email = Form.useWatch('email', form);
  const phone = Form.useWatch('phone', form);
  const firstname = Form.useWatch('firstname', form);
  const lastname = Form.useWatch('lastname', form);
  const role = Form.useWatch('role', form);
  const identified = hasContactIdentity({ firstname, lastname, email, phone });

  const values = useMemo(
    () => initialValues,
    // Seeded once per opening; later typing lives in the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  React.useEffect(() => onRoleChange(role || ''), [role, onRoleChange]);

  const duplicate = useMemo(
    () => findDuplicateContact(knownContacts, { email, phone }),
    [knownContacts, email, phone],
  );
  // Someone at another customer: offered to link instead of creating them twice.
  const elsewhere = usePartnerDuplicateContact({ email, phone }, customerId);

  const submit = async () => {
    const fields = await form.validateFields();
    const data: ContactPayload = {
      firstname: fields.firstname?.trim() || null,
      lastname: fields.lastname?.trim() || null,
      email: fields.email?.trim() || null,
      phone: fields.phone || null,
      gender: fields.gender || null,
      locale: fields.locale || null,
      role: fields.role?.trim() || null,
    };
    onSubmit(data);
  };

  return (
    <div className={styles.panel} onKeyDown={stopEscape(onBack)}>
      <ContactFields
        form={form}
        values={values}
        showRole
        roleSuggestions={roleSuggestions}
        warningField={duplicate?.field}
      />

      {duplicate && (
        <Alert
          type="warning"
          className={styles.duplicateAlert}
          title={
            <span>
              <F
                id="component.contacts.duplicate"
                defaultMessage="{field, select, email {Email} other {Phone}} already belongs to {name}."
                values={{ field: duplicate.field, name: <b>{getContactName(duplicate.contact)}</b> }}
              />{' '}
              <Button type="link" size="small" onClick={() => onLinkInstead(duplicate.contact)}>
                {getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.VIEW })}
              </Button>
            </span>
          }
        />
      )}

      {!duplicate && elsewhere && (
        <DuplicateElsewhereNotice duplicate={elsewhere} onLink={onLinkInstead} className={styles.duplicateAlert} />
      )}

      <div className={styles.panelFooter}>
        <span className={styles.panelHint}>
          {!identified && (
            <F id="component.contacts.identityRequired" defaultMessage="Enter a name, email or phone." />
          )}
        </span>
        <span className={styles.panelActions}>
          <Button onClick={onBack} disabled={busy}>
            <F id="pages.emailCenter.templates.create.backAction" defaultMessage="Back" />
          </Button>
          <Button
            type="primary"
            loading={busy}
            disabled={!identified || Boolean(duplicate)}
            onClick={submit}
          >
            <F id="component.contacts.createAndAdd" defaultMessage="Create & add" />
          </Button>
        </span>
      </div>
    </div>
  );
};

export default NewContactStep;
